import classNames from "classnames";
import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { debounce } from "throttle-debounce";
import { useStorageObjectVersions } from "../../../common/hooks/use-storage-object-versions";
import { VersioningBlock } from "../../../common/VersioningBlock";
import type { VersioningActions } from "../../../common/VersioningBlock/types";
import { RestoreVersionModal } from "../../../common/VersioningBlock/VersioningBlockModals/RestoreVersionsModal";
import { SaveVersionModal } from "../../../common/VersioningBlock/VersioningBlockModals/SaveVersionModal";
import type {
  ClassifierValue,
  ClassPostObjType,
  Label,
} from "../../../services/api/instances/classificatorApi/types";

import { useAppDispatch } from "../../../services/redux/hooks/use-dispatch";
import { useAppSelector } from "../../../services/redux/hooks/use-selector";
import { AppButton } from "../../../shared/components/AppButton";
import { AppUnderlinedTitle } from "../../../shared/components/AppUnderlinedTitle";
import { PlusIcon } from "../../../shared/icons/PlusIcon";
import { useClassifier } from "../hooks/use-classifier";
import { updateStorageObjectAction } from "../redux/actions/actions";
import {
  createClassifierObjectVersion,
  deleteClassifierObjectVersion,
  updateClassifierObjectVersion,
} from "../redux/actions/storage-versioning-actions";
import {
  selectClassifierObjectVersions,
  selectSelectedClassifier,
  selectSelectedClassifierObjectVersion,
} from "../redux/selectors";
import {
  setClassifierObjectVersionsAction,
  setClassifiersAction,
  setSelectedClassifierAction,
  setSelectedClassifierObjectVersionAction,
} from "../redux/slice";
import { ClassCommonSetting } from "./ClassCommonSetting";
import { ClassReviewCard } from "./ClassReviewCard";
import styles from "./styles.module.css";
import { CopyButton } from "../../../common/Components/Buttons/CopyButton";
import {
  StorageVersionActions,
  StorageObject,
  StorageObjectVersion,
} from "../../../services/api/instances/storageApi/types/object_types";
import { generateId } from "../../../shared/utils/generateId";

const CLASSIFIER_VERSION_ACTIONS: StorageVersionActions<ClassifierValue> = {
  createVersion: createClassifierObjectVersion,
  updateVersion: updateClassifierObjectVersion,
  deleteVersion: deleteClassifierObjectVersion,
  updateObject: updateStorageObjectAction,
  setSelectedObject: setSelectedClassifierAction,
  setObjectVersions: setClassifierObjectVersionsAction,
  setSelectedVersion: setSelectedClassifierObjectVersionAction,
};

interface Props {
  leftData: ClassPostObjType;
  updateClassifier: (postObj: StorageObject<ClassifierValue>) => void;
}

export const ClassLeftContainer: React.FC<Props> = ({
  leftData,
  updateClassifier,
}) => {
  const dispatch = useAppDispatch();
  const classifierVersions = useAppSelector(selectClassifierObjectVersions);
  const selectedClassifierVersion = useAppSelector(
    selectSelectedClassifierObjectVersion,
  );
  const selectedClassifier = useAppSelector(selectSelectedClassifier);
  const {
    createObjectVersion,
    updateObjectVersion,
    restoreObjectVersion,
    refreshVersion,
    deleteObjectVersion,
  } = useStorageObjectVersions<ClassifierValue>(
    classifierVersions,
    selectedClassifierVersion,
    selectedClassifier,
    CLASSIFIER_VERSION_ACTIONS,
  );
  const { onNameChange, storage, compareClassiFierValue } = useClassifier();
  const [openVersionModal, setOpenVersionModal] = useState(false);
  const [modalType, setModalType] = useState<string>("");
  const { t } = useTranslation();

  const getRefText = () => {
    return `ID: ${selectedClassifier?.ref.slice(
      0,
      6,
    )}*************${selectedClassifier?.ref.slice(-6)}`;
  };

  const handleOpenVersionModal = (type: VersioningActions) => {
    setModalType(type);
    setOpenVersionModal(true);
  };

  const handleCloseVersionModal = () => {
    setModalType("");
    setOpenVersionModal(false);
  };

  const classifierUpdate = useCallback(
    debounce(1000, (postObj: StorageObject<ClassifierValue>) => {
      updateClassifier(postObj);
    }),
    [],
  );

  const getNextBody = (labels: Label[]) => {
    const updatedClassifierData = {
      ...selectedClassifier?.value.classifier_object,
      labels,
    };
    dispatch(
      setSelectedClassifierAction({
        ...selectedClassifier,
        value: {
          classifier_object: updatedClassifierData,
        },
      } as StorageObject<ClassifierValue>),
    );
    const updatedClassifiers = storage.updateById(
      selectedClassifier?.id as string,
      {
        value: {
          classifier_object: updatedClassifierData,
        },
      } as StorageObject<ClassifierValue>,
      true,
    );

    dispatch(
      setClassifiersAction(
        updatedClassifiers as StorageObject<ClassifierValue>[],
      ),
    );

    classifierUpdate({
      ...selectedClassifier,
      value: {
        classifier_object: updatedClassifierData,
      },
    } as StorageObject<ClassifierValue>);
  };

  const handleDeleteVariant = (variantId: string) => {
    const nextLabels = leftData.labels.filter((item) => item.id !== variantId);

    getNextBody(nextLabels);
  };

  const handleDeleteSample = (variantId: string, sampleId: string) => {
    const nextLabels = leftData.labels.map((item) => {
      if (item.id === variantId) {
        return {
          ...item,
          samples: item.samples.filter((item) => item.id !== sampleId),
        };
      } else {
        return item;
      }
    });

    getNextBody(nextLabels);
  };

  const handleAddCardVariant = () => {
    const newCard = {
      label: "",
      description: "",
      id: generateId(),
      samples: [],
    };

    getNextBody([...leftData.labels, newCard]);
  };

  const handleAddSample = (variantId: string) => {
    const nextLabels = leftData.labels.map((label) => {
      if (variantId === label.id) {
        return {
          ...label,
          samples: [
            ...label.samples,
            {
              value: "",
              id: generateId(),
            },
          ],
        };
      } else {
        return label;
      }
    });

    getNextBody(nextLabels);
  };

  const handleVariantLabelChange = (value: string, variantId: string) => {
    const nextLabels = leftData.labels.map((item) => {
      if (item.id === variantId) {
        return {
          ...item,
          label: value,
        };
      } else {
        return item;
      }
    });

    getNextBody(nextLabels);
  };

  const handleVariantDescriptionChange = (value: string, variantId: string) => {
    const nextLabels = leftData.labels.map((item) => {
      if (item.id === variantId) {
        return {
          ...item,
          description: value,
        };
      } else {
        return item;
      }
    });

    getNextBody(nextLabels);
  };

  const handleSampleInputChange = (
    value: string,
    variantId: string,
    sampleId: string,
  ) => {
    const nextLabels = leftData.labels.map((label) => {
      if (variantId === label.id) {
        return {
          ...label,
          samples: label.samples.map((sample) => {
            if (sample.id === sampleId) {
              return {
                ...sample,
                value,
              };
            } else {
              return sample;
            }
          }),
        };
      } else {
        return label;
      }
    });

    getNextBody(nextLabels);
  };

  return (
    <div className={styles["left-container"]}>
      <ClassCommonSetting
        onChange={onNameChange}
        name={selectedClassifier?.friendly_name as string}
      />
      <div className={styles["info-container"]}>
        <div className={styles.idContainer}>
          <div>{getRefText()}</div>
          <CopyButton copyContent={selectedClassifier?.ref || ""} />
        </div>
        <VersioningBlock
          versions={classifierVersions}
          selectedVersion={selectedClassifierVersion}
          onEditName={updateObjectVersion}
          onRefreshVersion={refreshVersion}
          onOpenSaveVersionModal={(type) => handleOpenVersionModal(type)}
          onOpenRestoreVersionModal={(type) => handleOpenVersionModal(type)}
          onDeleteVersion={deleteObjectVersion}
          isSaveButtonDisabled={compareClassiFierValue()}
        />
      </div>
      <AppUnderlinedTitle leftContent="Варианты" className={styles["title"]}>
        <AppButton
          small
          icon={<PlusIcon />}
          buttonType="text"
          className={classNames(styles.button, styles["add-button"])}
          onClick={handleAddCardVariant}
        >
          {t("classification.add_button")}
        </AppButton>
      </AppUnderlinedTitle>
      <div className={styles["card-container"]}>
        {leftData?.labels?.map((item: Label, index) => (
          <ClassReviewCard
            key={(item as any).id}
            card={item as Label}
            className={styles.card}
            handleDeleteVariant={handleDeleteVariant}
            handleDeleteSample={handleDeleteSample}
            handleAddSample={handleAddSample}
            handleVariantLabelChange={handleVariantLabelChange}
            handleVariantDescriptionChange={handleVariantDescriptionChange}
            handleSampleInputChange={handleSampleInputChange}
          />
        ))}
      </div>
      <SaveVersionModal
        selectedVersion={
          selectedClassifierVersion as StorageObjectVersion<ClassifierValue>
        }
        createVersion={createObjectVersion}
        modalType={modalType}
        closeModal={handleCloseVersionModal}
      />
      <RestoreVersionModal
        versions={classifierVersions}
        closeModal={handleCloseVersionModal}
        modalType={modalType}
        onOk={restoreObjectVersion}
      />
    </div>
  );
};
